import {
	ApolloClient,
	ApolloLink,
	createHttpLink,
	from,
	// fromPromise,
	HttpLink,
} from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry/retryLink';
import { Observable } from '@apollo/client/utilities';
//import fetch from "isomorphic-fetch";

import { onError } from '@apollo/client/link/error';
import { cache } from './cache';
import { captureAPIError } from '../../utils/sentry-helpers';
import { FirebaseAuth } from '../firebase/firebase';

// Links
// ===========================================================================
//
// SEE  https://www.apollographql.com/docs/react/data/error-handling/
//      https://www.apollographql.com/docs/react/api/link/introduction/#composing-a-link-chain
//

export const Apolloclient = (token?: any) => {
	const authLinkHasura = new ApolloLink((operation, forward) => {
		if (typeof window !== 'undefined') {
			const accessToken = token ? token : localStorage.getItem('accessToken');

			operation.setContext(({ headers }: any) => {
				const newHeaders: any = { ...headers };
				// Solo incluir Authorization si hay un token válido
				if (accessToken && accessToken.trim() !== '') {
					newHeaders.Authorization = `Bearer ${accessToken}`;
				}
				return { headers: newHeaders };
			});

			return forward(operation);
		}

		return null;
	});

	// const errorLink = onError(
	//   ({ graphQLErrors, networkError, operation, forward }) => {
	//     if (graphQLErrors) {
	//       console.log('graphQLErrors',graphQLErrors)
	//       for (let err of graphQLErrors) {
	//         switch (err.statusCode) {
	//           case 401:
	//             return fromPromise(
	//               getNewToken().catch((error) => {
	//                 console.log("[Apollo] error link", error);
	//                 // Handle token refresh errors e.g clear stored tokens, redirect to login
	//                 return;
	//               })
	//             )
	//               .filter((value) => Boolean(value))
	//               .flatMap((accessToken) => {
	//                 const oldHeaders = operation.getContext().headers;
	//                 // modify the operation context with a new token
	//                 operation.setContext({
	//                   headers: {
	//                     ...oldHeaders,
	//                     authorization: `Bearer ${accessToken}`,
	//                   },
	//                 });

	//                 // retry the request, returning the new observable
	//                 return forward(operation);
	//               });
	//         }
	//       }
	//     }
	//   }
	// );

	const errorLink: any = onError((error) => {
		const { graphQLErrors, networkError, operation, forward } = error;
		// Manejar errores de JWT expirado
		if (graphQLErrors && graphQLErrors.length > 0) {
			const jwtExpiredError = graphQLErrors.find(
				(err: any) => err.message?.includes('JWTExpired') || err.message?.includes('Could not verify JWT')
			);

			// También detectar errores de campos no encontrados que pueden indicar token expirado
			const fieldNotFoundError = graphQLErrors.find(
				(err: any) => err.message?.includes("field 'user' not found") || err.message?.includes("field 'personalInformation' not found")
			);

			if ((jwtExpiredError || fieldNotFoundError) && typeof window !== 'undefined') {
				const user = FirebaseAuth.currentUser;
				if (user) {
					// Refrescar el token y reintentar la operación
					return new Observable((observer: any) => {
						let subscriber: any = null;

						user
							.getIdToken(true)
							.then((newToken: string) => {
								// Actualizar el token en localStorage
								localStorage.setItem('accessToken', newToken);

								// Actualizar los headers con el nuevo token
								const oldHeaders = operation.getContext().headers;
								operation.setContext({
									headers: {
										...oldHeaders,
										Authorization: `Bearer ${newToken}`,
									},
								});

								// Reintentar la operación con el nuevo token
								subscriber = forward(operation).subscribe({
									next: observer.next.bind(observer),
									error: observer.error.bind(observer),
									complete: observer.complete.bind(observer),
								});
							})
							.catch((error: any) => {
								console.error('[Apollo] Error refreshing token:', error);
								// Si no se puede refrescar, limpiar token pero no redirigir automáticamente
								// Las páginas protegidas manejarán su propia redirección
								localStorage.removeItem('accessToken');
								// No propagar el error al usuario, solo completar
								observer.complete();
							});

						// Retornar función de limpieza
						return () => {
							if (subscriber) {
								subscriber.unsubscribe();
							}
						};
					});
				} else {
					// No hay usuario autenticado, limpiar token pero no redirigir automáticamente
					// Las páginas protegidas manejarán su propia redirección
					console.warn('[Apollo] JWT expired but no authenticated user found. Clearing token.');
					localStorage.removeItem('accessToken');
					// Retornar un Observable que completa sin error para evitar propagación
					return new Observable((observer: any) => {
						observer.complete();
					});
				}
			}

			// Manejar otros errores de GraphQL (excluyendo errores de JWT expirado)
			const nonJwtErrors = graphQLErrors.filter(
				(err: any) =>
					!err.message?.includes('JWTExpired') &&
					!err.message?.includes('Could not verify JWT') &&
					!err.message?.includes("field 'user' not found") &&
					!err.message?.includes("field 'personalInformation' not found")
			);

			nonJwtErrors.forEach(({ message, locations, path }: any) => {
				console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);

				// Capturar en Sentry solo errores que no sean de JWT expirado
				const errorObj = new Error(`GraphQL Error: ${message}`);
				captureAPIError(errorObj, operation.operationName, {
					graphQLError: message,
					path,
					locations,
					query: operation.query.loc?.source.body,
					variables: operation.variables,
				});
			});
		}

		if (networkError) {
			console.log(`[Network error]: ${networkError}`);

			// Capturar errores de red en Sentry
			captureAPIError(networkError as Error, operation.operationName, {
				type: 'network',
				query: operation.query.loc?.source.body,
				variables: operation.variables,
			});
		}
	});

	// Una sola fuente de verdad: evita GATSBY_* vs REACT_APP_* apuntando a entornos distintos.
	const hasuraUri = process.env.GATSBY_HASURA_URL || process.env.REACT_APP_BACKEND_URL;

	const endpointHasura = new HttpLink({
		uri: hasuraUri,
		fetch,
		fetchOptions: {
			reconnect: true,
		},
	});

	const accessToken = token ? token : typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

	const backendHeaders: Record<string, string> = {};
	// Solo incluir Authorization si hay un token válido
	if (accessToken && accessToken.trim() !== '') {
		backendHeaders.Authorization = `Bearer ${accessToken}`;
	}

	const directionalLink = new RetryLink().split(
		(operation) => operation.getContext().clientName === 'third-party',
		from([authLinkHasura, endpointHasura]),

		new RetryLink().split(
			(operation) => operation.getContext().clientName === 'contracts',
			createHttpLink({
				fetch,
				uri: 'http://localhost:8000/subgraphs/name/scaffold-eth/your-contract',
			}),
			createHttpLink({
				uri: hasuraUri,
				fetch,
				headers: backendHeaders,
			})
		)
	);

	return new ApolloClient({
		link: from([errorLink, directionalLink]),
		// ssrMode: true,
		cache,
	});
};
